import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Spinner,
  Input,
  Table,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Notification } from "../common/style";
import {
  manageProducts,
  updateProduct,
  deleteProduct,
  cloneProduct,
} from "../product/service";

const CatalogManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    hasMore: true,
    total: 0,
  });
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [visibleCarcassExtensions, setVisibleCarcassExtensions] = useState(
    new Set()
  );
  const [productInfo, setProductInfo] = useState({
    name: "",
    external_structure: "",
    listing_category: "",
    listing_sub_category: "",
    carcassExtensions: {
      top: { front: "", back: "", left: "", right: "" },
      bottom: { front: "", back: "", left: "", right: "" },
      left: { front: "", back: "", top: "", bottom: "" },
      right: { front: "", back: "", top: "", bottom: "" },
    },
    shutterExtensions: {
      top: "",
      bottom: "",
    },
    imagesToRemove: [],
  });
  const [isProductUpdating, setIsProductUpdating] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [productToClone, setProductToClone] = useState(null);
  const [replaceTemplate, setReplaceTemplate] = useState(null);
  const [orderItemId, setOrderItemId] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const editModalFileInputRef = useRef(null);
  const observerRef = useRef(null);
  const currentPageRef = useRef(1);
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);

  const carcassExtensionOptions = [
    { key: "top.front", label: "Top Front Panel", side: "top", dir: "front" },
    { key: "top.back", label: "Top Back Panel", side: "top", dir: "back" },
    { key: "top.left", label: "Top Left Panel", side: "top", dir: "left" },
    { key: "top.right", label: "Top Right Panel", side: "top", dir: "right" },
    {
      key: "bottom.front",
      label: "Bottom Front Panel",
      side: "bottom",
      dir: "front",
    },
    {
      key: "bottom.back",
      label: "Bottom Back Panel",
      side: "bottom",
      dir: "back",
    },
    {
      key: "bottom.left",
      label: "Bottom Left Panel",
      side: "bottom",
      dir: "left",
    },
    {
      key: "bottom.right",
      label: "Bottom Right Panel",
      side: "bottom",
      dir: "right",
    },
    {
      key: "left.front",
      label: "Left Front Panel",
      side: "left",
      dir: "front",
    },
    { key: "left.back", label: "Left Back Panel", side: "left", dir: "back" },
    { key: "left.top", label: "Left Top Panel", side: "left", dir: "top" },
    {
      key: "left.bottom",
      label: "Left Bottom Panel",
      side: "left",
      dir: "bottom",
    },
    {
      key: "right.front",
      label: "Right Front Panel",
      side: "right",
      dir: "front",
    },
    {
      key: "right.back",
      label: "Right Back Panel",
      side: "right",
      dir: "back",
    },
    { key: "right.top", label: "Right Top Panel", side: "right", dir: "top" },
    {
      key: "right.bottom",
      label: "Right Bottom Panel",
      side: "right",
      dir: "bottom",
    },
  ];

  // Fetch initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch products when search filter changes (debounced, but not on initial load)
  useEffect(() => {
    if (!isInitialMountRef.current && searchTerm !== undefined) {
      // Reset to page 1 when search term changes and reload
      const timeoutId = setTimeout(() => {
        currentPageRef.current = 1;
        setPagination((prev) => ({ ...prev, page: 1, hasMore: true }));
        setProducts([]);
        loadProducts();
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setIsNotificationVisible(true);
    setTimeout(() => {
      setIsNotificationVisible(false);
    }, 5000);
  };

  const searchTermRef = useRef(searchTerm);

  // Keep searchTermRef in sync with searchTerm
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const loadProducts = useCallback(async (isLoadMore = false) => {
    // Prevent concurrent calls
    if (isLoadingRef.current) return;

    // Prevent load more until initial load is complete
    if (isLoadMore && isInitialMountRef.current) return;

    try {
      isLoadingRef.current = true;

      // Only set main loading if it's not a load more operation
      if (!isLoadMore) setLoading(true);
      else setLoadingMore(true);

      const page = isLoadMore ? currentPageRef.current + 1 : 1;
      const currentSearchTerm = searchTermRef.current;
      const response = await manageProducts(page, 12, currentSearchTerm);
      const productsData = response.products || [];
      const paginationData = response.pagination || {};

      if (isLoadMore) setProducts((prev) => [...prev, ...productsData]);
      else {
        setProducts(productsData);
      }

      // Update pagination state and ref
      const newPage = isLoadMore ? currentPageRef.current + 1 : 1;
      currentPageRef.current = newPage;
      const currentPage = paginationData.current_page || newPage;
      const totalPages = paginationData.total_pages || 1;
      setPagination((prev) => ({
        page: newPage,
        limit: 12,
        hasMore: currentPage < totalPages, // Check if there are more pages
        total:
          paginationData.total_items ||
          (isLoadMore ? prev.total : productsData.length),
      }));
    } catch (error) {
      console.error("Error loading products:", error);
      showNotification(
        "error",
        error?.error || "Failed to load products. Please try again."
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  const fetchInitialData = async () => {
    // Only fetch if this is the initial load
    if (!isInitialMountRef.current) return;

    try {
      await loadProducts();
      // Mark initial load as complete after async operations finish
      isInitialMountRef.current = false;
    } catch (error) {
      console.error("Error loading initial data:", error);
      // Still mark as complete even if there's an error
      isInitialMountRef.current = false;
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 0) {
      const validImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      const invalidFiles = files.filter(
        (file) => !validImageTypes.includes(file.type)
      );

      if (invalidFiles.length > 0) {
        showNotification(
          "error",
          "Please select valid image files (JPEG, PNG, or GIF)"
        );

        if (editModalFileInputRef.current)
          editModalFileInputRef.current.value = "";

        return;
      }

      setSelectedImageFiles((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveNewImage = (index) => {
    setSelectedImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageId) => {
    setProductInfo((prev) => ({
      ...prev,
      imagesToRemove: [...prev.imagesToRemove, imageId],
    }));
  };

  const handleRestoreImage = (imageId) => {
    setProductInfo((prev) => ({
      ...prev,
      imagesToRemove: prev.imagesToRemove.filter((id) => id !== imageId),
    }));
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    // Initialize with existing values if available
    const carcassExt = product.carcass_extensions || {};
    const visibleExts = new Set();

    // Check which extensions have values and add them to visible set
    const extensionMap = [
      { key: "top.front", label: "Top Front Panel", side: "top", dir: "front" },
      { key: "top.back", label: "Top Back Panel", side: "top", dir: "back" },
      { key: "top.left", label: "Top Left Panel", side: "top", dir: "left" },
      { key: "top.right", label: "Top Right Panel", side: "top", dir: "right" },
      {
        key: "bottom.front",
        label: "Bottom Front Panel",
        side: "bottom",
        dir: "front",
      },
      {
        key: "bottom.back",
        label: "Bottom Back Panel",
        side: "bottom",
        dir: "back",
      },
      {
        key: "bottom.left",
        label: "Bottom Left Panel",
        side: "bottom",
        dir: "left",
      },
      {
        key: "bottom.right",
        label: "Bottom Right Panel",
        side: "bottom",
        dir: "right",
      },
      {
        key: "left.front",
        label: "Left Front Panel",
        side: "left",
        dir: "front",
      },
      { key: "left.back", label: "Left Back Panel", side: "left", dir: "back" },
      { key: "left.top", label: "Left Top Panel", side: "left", dir: "top" },
      {
        key: "left.bottom",
        label: "Left Bottom Panel",
        side: "left",
        dir: "bottom",
      },
      {
        key: "right.front",
        label: "Right Front Panel",
        side: "right",
        dir: "front",
      },
      {
        key: "right.back",
        label: "Right Back Panel",
        side: "right",
        dir: "back",
      },
      { key: "right.top", label: "Right Top Panel", side: "right", dir: "top" },
      {
        key: "right.bottom",
        label: "Right Bottom Panel",
        side: "right",
        dir: "bottom",
      },
    ];

    extensionMap.forEach((ext) => {
      const value = carcassExt[ext.side]?.[ext.dir];
      if (value !== undefined && value !== null && value !== "") {
        visibleExts.add(ext.key);
      }
    });

    setProductInfo({
      name: product.name || "",
      external_structure: product.external_structure || "",
      listing_category: product.listing_category || "",
      listing_sub_category: product.listing_sub_category || "",
      carcassExtensions: {
        top: {
          front: carcassExt.top?.front?.toString() || "",
          back: carcassExt.top?.back?.toString() || "",
          left: carcassExt.top?.left?.toString() || "",
          right: carcassExt.top?.right?.toString() || "",
        },
        bottom: {
          front: carcassExt.bottom?.front?.toString() || "",
          back: carcassExt.bottom?.back?.toString() || "",
          left: carcassExt.bottom?.left?.toString() || "",
          right: carcassExt.bottom?.right?.toString() || "",
        },
        left: {
          front: carcassExt.left?.front?.toString() || "",
          back: carcassExt.left?.back?.toString() || "",
          top: carcassExt.left?.top?.toString() || "",
          bottom: carcassExt.left?.bottom?.toString() || "",
        },
        right: {
          front: carcassExt.right?.front?.toString() || "",
          back: carcassExt.right?.back?.toString() || "",
          top: carcassExt.right?.top?.toString() || "",
          bottom: carcassExt.right?.bottom?.toString() || "",
        },
      },
      shutterExtensions: {
        top: product.shutter_extensions?.top?.toString() || "",
        bottom: product.shutter_extensions?.bottom?.toString() || "",
      },
      imagesToRemove: [],
    });
    setVisibleCarcassExtensions(visibleExts);
    setSelectedImageFiles([]);

    if (editModalFileInputRef.current) editModalFileInputRef.current.value = "";

    setIsEditModalOpen(true);
  };

  const toggleEditModal = () => {
    if (!isProductUpdating) {
      setIsEditModalOpen(false);
      setEditingProduct(null);
      setProductInfo({
        name: "",
        external_structure: "",
        listing_category: "",
        listing_sub_category: "",
        carcassExtensions: {
          top: { front: "", back: "", left: "", right: "" },
          bottom: { front: "", back: "", left: "", right: "" },
          left: { front: "", back: "", top: "", bottom: "" },
          right: { front: "", back: "", top: "", bottom: "" },
        },
        shutterExtensions: {
          top: "",
          bottom: "",
        },
        imagesToRemove: [],
      });
      setVisibleCarcassExtensions(new Set());
      setSelectedImageFiles([]);

      if (editModalFileInputRef.current)
        editModalFileInputRef.current.value = "";
    }
  };

  const handleAddCarcassExtension = (key) => {
    if (key && !visibleCarcassExtensions.has(key))
      setVisibleCarcassExtensions((prev) => new Set([...prev, key]));
  };

  const handleRemoveCarcassExtension = (key) => {
    setVisibleCarcassExtensions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
    // Clear the value when removing
    const option = carcassExtensionOptions.find((opt) => opt.key === key);

    if (option) {
      setProductInfo((prev) => ({
        ...prev,
        carcassExtensions: {
          ...prev.carcassExtensions,
          [option.side]: {
            ...prev.carcassExtensions[option.side],
            [option.dir]: "",
          },
        },
      }));
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      setIsProductUpdating(true);

      const shutterExtensions = {
        top: productInfo.shutterExtensions.top
          ? parseFloat(productInfo.shutterExtensions.top)
          : null,
        bottom: productInfo.shutterExtensions.bottom
          ? parseFloat(productInfo.shutterExtensions.bottom)
          : null,
      };

      // Process carcass extensions - convert empty strings to null and parse numbers
      const carcassExtensions = {};

      Object.keys(productInfo.carcassExtensions).forEach((side) => {
        carcassExtensions[side] = {};

        Object.keys(productInfo.carcassExtensions[side]).forEach(
          (direction) => {
            const value = productInfo.carcassExtensions[side][direction];

            if (value && value.trim() !== "")
              carcassExtensions[side][direction] = parseFloat(value) || value;
            else carcassExtensions[side][direction] = null;
          }
        );
      });

      const response = await updateProduct(
        editingProduct.id,
        null, // No status update
        selectedImageFiles.length > 0 ? selectedImageFiles : null, // Image files if provided
        shutterExtensions,
        productInfo.name !== editingProduct.name ? productInfo.name : null, // Name if changed
        productInfo.imagesToRemove.length > 0
          ? productInfo.imagesToRemove
          : null, // Image IDs to remove
        carcassExtensions,
        {
          external_structure:
            productInfo.external_structure !== editingProduct.external_structure
              ? productInfo.external_structure
              : null,
          listing_category:
            productInfo.listing_category !== editingProduct.listing_category
              ? productInfo.listing_category
              : null,
          listing_sub_category:
            productInfo.listing_sub_category !==
            editingProduct.listing_sub_category
              ? productInfo.listing_sub_category
              : null,
        }
      );
      const updatedProduct = response.product;

      setProducts((prev) =>
        prev.map((p) => {
          if (p.id === updatedProduct.id) return updatedProduct;

          return p;
        })
      );

      const updateMessages = [];

      if (selectedImageFiles.length > 0) updateMessages.push("image(s)");
      if (productInfo.imagesToRemove.length > 0)
        updateMessages.push("removed image(s)");
      if (productInfo.name !== editingProduct.name) updateMessages.push("name");
      if (
        productInfo.shutterExtensions.top ||
        productInfo.shutterExtensions.bottom
      )
        updateMessages.push("shutter extensions");

      showNotification("success", "Updated successfully!");
      toggleEditModal();
    } catch (error) {
      console.error("Error updating product:", error);
      showNotification(
        "error",
        error?.error || "Failed to update product. Please try again."
      );
    } finally {
      setIsProductUpdating(false);
    }
  };

  const handlePublishClick = async (product) => {
    if (product.status !== "draft") return;

    try {
      setIsProductUpdating(true);

      const response = await updateProduct(product.id, "publish");
      const updatedProduct = response.product;

      // Update product in state
      setProducts((prev) =>
        prev.map((p) => {
          if (p.id === updatedProduct.id) return updatedProduct;
          return p;
        })
      );

      showNotification("success", "Product published successfully!");
    } catch (error) {
      console.error("Error publishing product:", error);
      showNotification(
        "error",
        error?.error || "Failed to publish product. Please try again."
      );
    } finally {
      setIsProductUpdating(false);
    }
  };

  const handleCloneClick = (product) => {
    setProductToClone(product);
    setReplaceTemplate(null);
    setOrderItemId("");
    setIsCloneModalOpen(true);
  };

  const toggleCloneModal = () => {
    if (!isCloning) {
      setIsCloneModalOpen(false);
      setProductToClone(null);
      setReplaceTemplate(null);
      setOrderItemId("");
    }
  };

  const handleCloneConfirm = async () => {
    if (!productToClone) return;

    try {
      setIsCloning(true);

      const payload = {};
      if (replaceTemplate === true && orderItemId) {
        payload.order_item_id = orderItemId;
      }

      const response = await cloneProduct(productToClone.id, payload);
      const clonedProduct = response.product;

      // Add cloned product to the products list
      setProducts((prev) => [clonedProduct, ...prev]);

      // Update pagination total
      setPagination((prev) => ({
        ...prev,
        total: prev.total + 1,
      }));

      showNotification("success", "Product cloned successfully!");
      toggleCloneModal();
    } catch (error) {
      console.error("Error cloning product:", error);
      showNotification(
        "error",
        error?.error || "Failed to clone product. Please try again."
      );
    } finally {
      setIsCloning(false);
    }
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  const toggleDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setProductToDelete(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      setIsDeleting(true);

      if (productToDelete.status === "published") {
        // Update status to archive
        const response = await updateProduct(
          productToDelete.id,
          "archive" // Status update
        );
        const updatedProduct = response.product;

        // Update product in state
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id === updatedProduct.id) return updatedProduct;
            return p;
          })
        );

        showNotification("success", "Product archived successfully!");
      } else if (productToDelete.status === "draft") {
        // Delete the product
        await deleteProduct(productToDelete.id);

        // Remove product from state
        setProducts((prev) => prev.filter((p) => p.id !== productToDelete.id));

        showNotification("success", "Product deleted successfully!");
      }

      toggleDeleteModal();
    } catch (error) {
      console.error("Error deleting/archiving product:", error);
      showNotification(
        "error",
        error?.error || "Failed to delete/archive product. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const loadMoreRef = useCallback(
    (node) => {
      // Don't set up observer if still loading or initial load not complete
      if (loadingMore || loading || isInitialMountRef.current) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          pagination.hasMore &&
          !loadingMore &&
          !loading &&
          !isInitialMountRef.current
        )
          loadProducts(true);
      });

      if (node) observerRef.current.observe(node);
    },
    [loadingMore, loading, pagination.hasMore, loadProducts]
  );

  return (
    <Container className="p-4">
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={() => setIsNotificationVisible(false)}
        >
          {notification.message}
        </Notification>
      )}
      <h1 className="mb-4">Catalog Manager</h1>

      <Row className="mb-3">
        <Col className="p-0">
          <Input
            type="text"
            className="rounded-1"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <div className="mt-4">
          {products.length === 0 ? (
            <div className="text-center my-5">
              <p>No products found</p>
            </div>
          ) : (
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-center">Image</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                  <th className="text-center">Clone</th>
                  <th className="text-center">Delete</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr key={index}>
                    <td className="align-middle">{product.name}</td>
                    <td className="text-center">
                      {product.product_images &&
                      product.product_images.length > 0 ? (
                        <img
                          src={product.product_images[0].url}
                          alt={product.name}
                          style={{ maxWidth: "100px", maxHeight: "100px" }}
                        />
                      ) : (
                        <span className="text-muted">No image</span>
                      )}
                    </td>
                    <td className="text-center text-capitalize align-middle">
                      {product.status}
                    </td>
                    <td className="text-center align-middle">
                      <div className="d-flex gap-2 justify-content-center">
                        {product.status === "draft" && (
                          <Button
                            color="primary"
                            outline="none"
                            size="sm"
                            onClick={() => handlePublishClick(product)}
                            title="Publish Product"
                            disabled={isProductUpdating}
                          >
                            Publish
                          </Button>
                        )}
                        <Button
                          color="secondary"
                          outline="none"
                          size="sm"
                          onClick={() => handleEditClick(product)}
                          title="Edit Product"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </Button>
                      </div>
                    </td>
                    <td className="text-center align-middle">
                      <Button
                        color="info"
                        outline="none"
                        size="sm"
                        onClick={() => handleCloneClick(product)}
                        title="Clone Product"
                      >
                        <i className="fa fa-copy"></i>
                      </Button>
                    </td>
                    <td className="text-center align-middle">
                      <Button
                        color="danger"
                        outline="none"
                        size="sm"
                        onClick={() => handleDeleteClick(product)}
                        title="Delete Product"
                      >
                        <i className="fa-regular fa-trash-can"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      {!loading && pagination.hasMore && products.length > 0 && (
        <div ref={loadMoreRef} className="text-center py-4">
          {loadingMore ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner color="primary" className="me-2" />
              <span>Loading more products...</span>
            </div>
          ) : (
            <div className="text-muted">Scroll to load more</div>
          )}
        </div>
      )}

      {/* End of Products Message */}
      {!pagination.hasMore && products.length > 0 && (
        <div className="text-center py-4">
          <div className="text-muted">
            <i className="fa fa-check-circle me-2"></i>
            All products loaded ({products.length} of {pagination.total})
          </div>
        </div>
      )}

      {/* Edit Product Offcanvas */}
      <Offcanvas
        isOpen={isEditModalOpen}
        toggle={toggleEditModal}
        direction="end"
        className="w-50"
      >
        <OffcanvasHeader toggle={toggleEditModal}>Edit Product</OffcanvasHeader>
        <OffcanvasBody>
          <div className="mb-3">
            <Label for="productName">Name</Label>
            <Input
              type="text"
              id="productName"
              placeholder="Enter product name"
              value={productInfo.name}
              onChange={(e) =>
                setProductInfo((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={isProductUpdating}
            />
          </div>

          <div className="mb-3">
            <Label>Images</Label>
            {editingProduct &&
              editingProduct.product_images &&
              editingProduct.product_images.length > 0 && (
                <div className="mb-2">
                  <div className="d-flex flex-wrap gap-2">
                    {editingProduct.product_images.map((image, imgIndex) => {
                      const isMarkedForRemoval =
                        productInfo.imagesToRemove.includes(image.id);
                      return (
                        <div
                          key={image.id || imgIndex}
                          className="position-relative"
                        >
                          <img
                            src={image.url}
                            alt={editingProduct.name}
                            style={{
                              maxWidth: "200px",
                              maxHeight: "200px",
                              opacity: isMarkedForRemoval ? 0.5 : 1,
                              border: isMarkedForRemoval
                                ? "2px solid red"
                                : "none",
                            }}
                            className="img-thumbnail"
                          />
                          <Button
                            color={isMarkedForRemoval ? "success" : "danger"}
                            size="sm"
                            className="position-absolute top-0 end-0 m-1"
                            onClick={() =>
                              isMarkedForRemoval
                                ? handleRestoreImage(image.id)
                                : handleRemoveExistingImage(image.id)
                            }
                            disabled={isProductUpdating}
                            style={{ fontSize: "12px", padding: "2px 6px" }}
                          >
                            <i
                              className={`fa ${
                                isMarkedForRemoval ? "fa-undo" : "fa-times"
                              }`}
                            ></i>
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            <div className="mb-2">
              <Label>Add New Images</Label>
              <Input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif"
                onChange={handleFileChange}
                innerRef={editModalFileInputRef}
                disabled={isProductUpdating}
                multiple
              />
            </div>
            {selectedImageFiles.length > 0 && (
              <div className="mt-2">
                <p className="small text-muted mb-2">New Images to Upload:</p>
                <div className="d-flex flex-wrap gap-2">
                  {selectedImageFiles.map((file, index) => (
                    <div key={index} className="position-relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{ maxWidth: "200px", maxHeight: "200px" }}
                        className="img-thumbnail"
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => handleRemoveNewImage(index)}
                        disabled={isProductUpdating}
                        style={{ fontSize: "12px", padding: "2px 6px" }}
                      >
                        <i className="fa fa-times"></i>
                      </Button>
                      <div
                        className="small text-muted mt-1"
                        style={{ maxWidth: "200px" }}
                      >
                        {file.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mb-3">
            <Label for="externalStructure">External Structure</Label>
            <Input
              type="text"
              id="externalStructure"
              placeholder="Enter external structure"
              value={productInfo.external_structure}
              onChange={(e) =>
                setProductInfo((prev) => ({
                  ...prev,
                  external_structure: e.target.value,
                }))
              }
              disabled={isProductUpdating}
            />
          </div>

          <div className="mb-3">
            <Label for="listingCategory">Listing Category</Label>
            <Input
              type="text"
              id="listingCategory"
              placeholder="Enter listing category"
              value={productInfo.listing_category}
              onChange={(e) =>
                setProductInfo((prev) => ({
                  ...prev,
                  listing_category: e.target.value,
                }))
              }
              disabled={isProductUpdating}
            />
          </div>

          <div className="mb-3">
            <Label for="listingSubCategory">Listing Sub Category</Label>
            <Input
              type="text"
              id="listingSubCategory"
              placeholder="Enter listing sub category"
              value={productInfo.listing_sub_category}
              onChange={(e) =>
                setProductInfo((prev) => ({
                  ...prev,
                  listing_sub_category: e.target.value,
                }))
              }
              disabled={isProductUpdating}
            />
          </div>

          <div className="mb-3">
            <Label className="mb-2">Carcass Extensions</Label>
            <div className="border rounded p-3">
              <div className="mb-3">
                <Label for="addCarcassExtension" className="small mb-2">
                  Add Extension
                </Label>
                <Input
                  type="select"
                  id="addCarcassExtension"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddCarcassExtension(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  disabled={isProductUpdating}
                >
                  <option value="">Select an extension to add...</option>
                  {carcassExtensionOptions
                    .filter((opt) => !visibleCarcassExtensions.has(opt.key))
                    .map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                </Input>
              </div>

              {Array.from(visibleCarcassExtensions).map((key) => {
                const option = carcassExtensionOptions.find(
                  (opt) => opt.key === key
                );
                if (!option) return null;

                return (
                  <div
                    key={key}
                    className="mb-2 d-flex align-items-center gap-2"
                  >
                    <div className="flex-grow-1">
                      <Label className="small mb-1">{option.label}</Label>
                      <Input
                        type="number"
                        placeholder="Enter value"
                        value={
                          productInfo.carcassExtensions[option.side][option.dir]
                        }
                        onChange={(e) =>
                          setProductInfo((prev) => ({
                            ...prev,
                            carcassExtensions: {
                              ...prev.carcassExtensions,
                              [option.side]: {
                                ...prev.carcassExtensions[option.side],
                                [option.dir]: e.target.value,
                              },
                            },
                          }))
                        }
                        disabled={isProductUpdating}
                      />
                    </div>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveCarcassExtension(key)}
                      disabled={isProductUpdating}
                      className="mt-4"
                    >
                      <i className="fa fa-times"></i>
                    </Button>
                  </div>
                );
              })}

              {visibleCarcassExtensions.size === 0 && (
                <p className="text-muted small mb-0">
                  No extensions added. Use the dropdown above to add one.
                </p>
              )}
            </div>
          </div>

          <div className="mb-3">
            <Label>Shutter Extensions</Label>
            <Row className="mt-2">
              <Col md={6}>
                <Label for="shutterTop">Top</Label>
                <Input
                  type="number"
                  id="shutterTop"
                  placeholder="Enter top extension"
                  value={productInfo.shutterExtensions.top}
                  onChange={(e) =>
                    setProductInfo((prev) => ({
                      ...prev,
                      shutterExtensions: {
                        ...prev.shutterExtensions,
                        top: e.target.value,
                      },
                    }))
                  }
                  disabled={isProductUpdating}
                />
              </Col>
              <Col md={6}>
                <Label for="shutterBottom">Bottom</Label>
                <Input
                  type="number"
                  id="shutterBottom"
                  placeholder="Enter bottom extension"
                  value={productInfo.shutterExtensions.bottom}
                  onChange={(e) =>
                    setProductInfo((prev) => ({
                      ...prev,
                      shutterExtensions: {
                        ...prev.shutterExtensions,
                        bottom: e.target.value,
                      },
                    }))
                  }
                  disabled={isProductUpdating}
                />
              </Col>
            </Row>
          </div>

          <div className="d-flex gap-2 mt-4 pt-3 border-top">
            <Button
              color="secondary"
              onClick={toggleEditModal}
              disabled={isProductUpdating}
              className="flex-fill"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleUpdateProduct}
              disabled={isProductUpdating}
              className="flex-fill"
            >
              {isProductUpdating ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>

      {/* Clone Confirmation Modal */}
      <Modal isOpen={isCloneModalOpen} toggle={toggleCloneModal}>
        <ModalHeader toggle={toggleCloneModal}>Clone Product</ModalHeader>
        <ModalBody>
          {productToClone && (
            <>
              <p className="mb-3">
                Do you want to replace the template for product{" "}
                <strong>{productToClone.name}</strong>?
              </p>
              <div className="d-flex gap-2 mb-3">
                <Button
                  color={replaceTemplate === true ? "primary" : "secondary"}
                  outline={replaceTemplate !== true}
                  onClick={() => setReplaceTemplate(true)}
                  disabled={isCloning}
                  className="flex-fill"
                >
                  Yes
                </Button>
                <Button
                  color={replaceTemplate === false ? "primary" : "secondary"}
                  outline={replaceTemplate !== false}
                  onClick={() => {
                    setReplaceTemplate(false);
                    setOrderItemId("");
                  }}
                  disabled={isCloning}
                  className="flex-fill"
                >
                  No
                </Button>
              </div>
              {replaceTemplate === true && (
                <div className="mt-3">
                  <Label for="orderItemId">Order Item ID</Label>
                  <Input
                    type="text"
                    id="orderItemId"
                    placeholder="Enter Order Item ID"
                    value={orderItemId}
                    onChange={(e) => setOrderItemId(e.target.value)}
                    disabled={isCloning}
                  />
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleCloneModal}
            disabled={isCloning}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleCloneConfirm}
            disabled={
              isCloning ||
              replaceTemplate === null ||
              (replaceTemplate === true && !orderItemId.trim())
            }
          >
            {isCloning ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cloning...
              </>
            ) : (
              "Clone"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>
          Confirm{" "}
          {productToDelete?.status === "published" ? "Archive" : "Delete"}
        </ModalHeader>
        <ModalBody>
          {productToDelete && (
            <p className="m-0">
              Are you sure you want to{" "}
              {productToDelete.status === "published" ? "archive" : "delete"}{" "}
              the product <strong>{productToDelete.name}</strong>?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleDeleteModal}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {productToDelete?.status === "published"
                  ? "Archiving..."
                  : "Deleting..."}
              </>
            ) : productToDelete?.status === "published" ? (
              "Archive"
            ) : (
              "Delete"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CatalogManager;
